import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { sincronizarFila } from "./envio";
import { aoMudarFila, listarFila, type VendaNaFila } from "./fila";

export function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
}

const INTERVALO_MS = 15_000;

/**
 * Estado da fila do usuário no aparelho, com envio automático: ao abrir, quando a internet
 * volta e a cada 15 s enquanto houver venda pendente.
 */
export function useFila(usuarioId: string) {
  const [itens, setItens] = useState<VendaNaFila[]>([]);
  const [sincronizando, setSincronizando] = useState(false);
  const online = useOnline();
  const qc = useQueryClient();

  const recarregar = useCallback(() => {
    listarFila(usuarioId).then(setItens, () => setItens([]));
  }, [usuarioId]);

  useEffect(() => {
    recarregar();
    return aoMudarFila(recarregar);
  }, [recarregar]);

  const sincronizar = useCallback(async () => {
    setSincronizando(true);
    try {
      const r = await sincronizarFila(usuarioId);
      if (r.enviadas > 0 || r.recusadas > 0) {
        qc.invalidateQueries({ queryKey: ["vendas"] });
        qc.invalidateQueries({ queryKey: ["cardapio"] });
      }
      return r;
    } finally {
      setSincronizando(false);
    }
  }, [usuarioId, qc]);

  const pendentes = itens.filter((v) => v.estado === "PENDENTE");
  const recusadas = itens.filter((v) => v.estado === "RECUSADA");
  const temPendentes = pendentes.length > 0;

  useEffect(() => {
    if (!temPendentes || !online) return;
    sincronizar();
    const t = setInterval(sincronizar, INTERVALO_MS);
    return () => clearInterval(t);
  }, [temPendentes, online, sincronizar]);

  return { itens, pendentes, recusadas, online, sincronizando, sincronizar };
}
